import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { deleteUserStyle } from '../../../../api/styles'

interface DeleteStyleDialogProps {
  isOpen: boolean
  onClose: () => void
  styleId: string
  styleName: string
  onDeleted: () => void
}

export function DeleteStyleDialog({
  isOpen,
  onClose,
  styleId,
  styleName,
  onDeleted,
}: DeleteStyleDialogProps) {
  const { t } = useTranslation('canvas')
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await deleteUserStyle(styleId)
      onDeleted()
      onClose()
    } catch (err) {
      console.error('Failed to delete style:', err)
      setIsDeleting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      data-testid="modal-overlay"
    >
      <div className="relative w-full max-w-md bg-surface-1 rounded-lg border border-border overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-medium text-foreground">{t('deleteStyleDialog.deleteStyle', { styleName })}</h2>
        </div>
        <div className="p-5">
          <p className="text-sm text-muted-foreground mb-4">
            {t('deleteStyleDialog.permanentRemove')}
          </p>
          <div className="flex gap-2 justify-end">
            <button
              onClick={onClose}
              disabled={isDeleting}
              className="px-3 py-1.5 text-sm rounded-md border border-border hover:bg-surface-2 transition-colors"
            >
              {t('deleteStyleDialog.cancel')}
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="px-3 py-1.5 text-sm rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
              data-testid="delete-style-confirm-btn"
            >
              {isDeleting ? t('deleteStyleDialog.deleting') : t('deleteStyleDialog.delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
